<template>
  <cst-page-template class="warning-banner-page" ds-root :hide-menu="true">
    <header-menu />
    <div class="ds-page ds-page--fill">
      <div class="ds-page__header">
        <h1>Waarschuwingsbanner beheer</h1>
        <ds-button data-qa="warning-banner-create-button" @click="onCreate">
          <svg viewBox="2 2 20 20" fill="currentColor" aria-hidden="true">
            <path
              d="M10.98 19.28V12.98H4.68V10.94H10.98V4.64H13.02V10.94H19.32V12.98H13.02V19.28H10.98Z"
            />
          </svg>
          Nieuwe banner
        </ds-button>
      </div>
      <p class="ds-page__intro">
        Je ziet en beheert hier alleen banners voor <strong>{{ currentBrandName }}</strong> en
        banners die voor alle merken gelden.
      </p>

      <div v-if="loading" class="ds-empty">
        <p class="ds-empty__message">Banners laden…</p>
      </div>

      <div v-else-if="!banners.length" class="ds-empty">
        <h2 class="ds-empty__title">Geen banners gevonden</h2>
        <p class="ds-empty__message">Maak een nieuwe banner aan om te beginnen.</p>
        <ds-button data-qa="warning-banner-create-first-button" @click="onCreate">
          Eerste banner aanmaken
        </ds-button>
      </div>

      <div v-else class="ds-table">
        <table-filter-chips
          v-if="activeFilterChips.length"
          :chips="activeFilterChips"
          @remove="removeFilter"
          @clear-all="clearFilters"
        />

        <div class="ds-table__scroll">
          <table :style="{ minWidth: tableMinWidth }">
            <sortable-table-header
              :columns="tableColumns"
              :filters="filters"
              :sort-column="sortColumn"
              :sort-direction="sortDirection"
              :has-actions="true"
              @toggle-sort="toggleSort"
              @set-filter="setFilter"
              @clear-filters="clearMultipleFilters"
            />
            <tbody>
              <tr v-for="banner in pagedBanners" :key="banner.Id">
                <td>
                  <span :class="getBrandBadgeClass(banner.BrandCode)">
                    {{ getBrandName(banner.BrandCode) }}
                  </span>
                </td>
                <td>
                  <span
                    class="ds-badge ds-badge--onColor"
                    :style="{ backgroundColor: banner.BackgroundColor }"
                  >
                    {{ translateColor(banner.BackgroundColor) }}
                  </span>
                </td>
                <td>
                  <div
                    class="warning-banner-page__text"
                    :title="stripHtml(banner.TextBlock)"
                    v-html="truncateText(banner.TextBlock)"
                  ></div>
                </td>
                <td>{{ formatDate(banner.DateFrom) }}</td>
                <td>
                  <span v-if="formatDate(banner.DateTo)">{{ formatDate(banner.DateTo) }}</span>
                  <span v-else class="warning-banner-page__nodate">Geen einddatum</span>
                </td>
                <td>{{ banner.UpdatedBy || '-' }}</td>
                <td>
                  <div class="ds-table__actions">
                    <button
                      class="ds-icon-button"
                      title="Wijzigen"
                      data-qa="warning-banner-edit-button"
                      @click="onEdit(banner)"
                    >
                      <svg viewBox="2 2 20 20" fill="currentColor" aria-hidden="true">
                        <path
                          d="M15.82 5.38C16.8 4.4 18.38 4.4 19.38 5.38C20.36 6.36 20.36 7.96 19.38 8.94L8.36 19.96L3.9 20.86L4.8 16.4L15.82 5.38ZM15.72 7.74L6.06 17.4L5.72 19.04L7.36 18.7L17.02 9.04L15.72 7.74Z"
                        />
                      </svg>
                    </button>
                    <button
                      class="ds-icon-button"
                      title="Verwijderen"
                      data-qa="warning-banner-delete-button"
                      @click="onDeleteClick(banner)"
                    >
                      <svg viewBox="2 2 20 20" fill="currentColor" aria-hidden="true">
                        <path
                          d="M6.42 19.84L6.06 6.46H3.72V4.92H8.3V3.88C8.3 2.76 9.18 1.84 10.48 1.84H13.52C14.82 1.84 15.7 2.76 15.7 3.88V4.92H20.28V6.46H17.94L17.58 19.84H6.42ZM10.06 4.06V4.92H13.94V4.06C13.94 3.66 13.6 3.32 13.2 3.32H10.8C10.4 3.32 10.06 3.66 10.06 4.06ZM7.96 18.3H16.04L16.28 6.46H7.72L7.96 18.3ZM9.58 17.22V7.54H11.08V17.22H9.58ZM12.92 17.22V7.54H14.42V17.22H12.92Z"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="!pagedBanners.length">
                <td colspan="7" class="ds-table__empty">
                  Geen resultaten gevonden voor de huidige filters.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <ds-pagination
          v-model:page="currentPage"
          v-model:page-size="pageSize"
          :total="filteredBanners.length"
        />
      </div>
    </div>

    <warning-banner-form
      v-if="showForm"
      :initial="selectedBanner"
      @cancel="closeForm"
      @saved="onBannerSaved"
    />

    <ds-confirm-dialog
      id="warning-banner-delete"
      :show="!!deleteConfirmBanner"
      title="Banner verwijderen"
      :body="deleteConfirmText"
      confirm-label="Verwijderen"
      cancel-label="Annuleren"
      danger
      :confirm-disabled="deleteInProgress"
      @confirm="onDeleteConfirm"
      @cancel="deleteConfirmBanner = null"
    />

    <div v-if="error" class="ds-toast-host">
      <div class="ds-toast ds-toast--error">
        <span>{{ error }}</span>
        <button class="ds-toast__close" aria-label="Sluiten" @click="error = null">&times;</button>
      </div>
    </div>
  </cst-page-template>
</template>

<script>
import warningBannerService from '@/services/warningBannerService'
import WarningBannerForm from './WarningBannerForm.vue'
import SortableTableHeader from './SortableTableHeader.vue'
import TableFilterChips from './TableFilterChips.vue'
import CstPageTemplate from '@/templates/CstPageTemplate.vue'
import HeaderMenu from '@/components/generic/presentational/molecules/headerMenu/HeaderMenu.vue'
import DsButton from '@/components/ds/atoms/dsButton/DsButton.vue'
import DsPagination from '@/components/ds/molecules/dsPagination/DsPagination.vue'
import DsConfirmDialog from '@/components/ds/molecules/dsConfirmDialog/DsConfirmDialog.vue'
import moment from 'moment'
import { mapState } from 'vuex'
import { getBrandBadgeClass } from '@/helpers/BrandHelper'

const BRAND_MAP = {
  '*': 'Alle merken',
  V: 'Volkswagen',
  N: 'Volkswagen bedrijfswagens',
  S: 'Seat',
  C: 'Skoda',
  A: 'Audi',
}

// Matches .ds-table__header-cell--actions.
const ACTIONS_COLUMN_WIDTH = 90

const COLOR_MAP = {
  red: 'Rood',
  dimgray: 'Licht grijs',
  orange: 'Oranje',
}

export default {
  name: 'WarningBannerPage',
  components: {
    CstPageTemplate,
    HeaderMenu,
    WarningBannerForm,
    SortableTableHeader,
    TableFilterChips,
    DsButton,
    DsPagination,
    DsConfirmDialog,
  },
  data() {
    return {
      banners: [],
      showForm: false,
      selectedBanner: null,
      deleteConfirmBanner: null,
      deleteInProgress: false,
      loading: false,
      error: null,
      filters: {
        BrandCode: '',
        BackgroundColor: '',
        TextBlock: '',
        DateFrom: '',
        DateTo: '',
        DateToFrom: moment().format('YYYY-MM-DD'),
        UpdatedBy: '',
      },
      sortColumn: 'DateFrom',
      sortDirection: 'desc',
      currentPage: 1,
      pageSize: 25,
    }
  },
  computed: {
    ...mapState('user', ['dealerSettings']),
    brandOptions: function () {
      return Object.entries(BRAND_MAP)
        .filter(([value]) => value === this.dealerSettings.brand || value === '*')
        .map(([value, label]) => ({ value, label }))
    },
    currentBrandName() {
      return this.getBrandName(this.dealerSettings.brand)
    },
    colorOptions() {
      return Object.entries(COLOR_MAP).map(([value, label]) => ({ value, label }))
    },
    deleteConfirmText() {
      return this.deleteConfirmBanner
        ? `Weet je zeker dat je de waarschuwingsbanner voor ${this.getBrandName(this.deleteConfirmBanner.BrandCode)} wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`
        : ''
    },
    tableColumns() {
      return [
        {
          key: 'BrandCode',
          label: 'Merk',
          width: '200px',
          filter: 'select',
          options: this.brandOptions,
        },
        {
          key: 'BackgroundColor',
          label: 'Kleur',
          width: '110px',
          filter: 'select',
          options: this.colorOptions,
        },
        {
          key: 'TextBlock',
          label: 'Tekst',
          width: '300px',
          filter: 'text',
          placeholder: 'Zoek tekst...',
        },
        { key: 'DateFrom', label: 'Begindatum', width: '155px', filter: 'date' },
        {
          key: 'DateTo',
          label: 'Einddatum',
          width: '155px',
          filter: 'date-range',
          rangeKey: 'DateToFrom',
        },
        {
          key: 'UpdatedBy',
          label: 'Gewijzigd door',
          width: '155px',
          filter: 'text',
          placeholder: 'Zoek gebruiker...',
        },
      ]
    },
    filteredBanners() {
      return this.banners.filter((banner) => {
        if (this.filters.DateToFrom) {
          const filterDate = moment(this.filters.DateToFrom, 'YYYY-MM-DD').startOf('day')
          if (banner.DateTo !== null && banner.DateTo !== undefined) {
            const endDate = moment.utc(banner.DateTo)
            if (endDate.isValid() && endDate.isBefore(filterDate)) return false
          }
        }
        if (this.filters.DateTo) {
          const filterDate = moment(this.filters.DateTo, 'YYYY-MM-DD').endOf('day')
          const bannerDate = moment.utc(banner.DateTo)
          if (!bannerDate.isValid() || bannerDate.isAfter(filterDate)) return false
        }
        if (this.filters.BrandCode && banner.BrandCode !== this.filters.BrandCode) return false
        if (this.filters.BackgroundColor && banner.BackgroundColor !== this.filters.BackgroundColor)
          return false
        if (
          this.filters.TextBlock &&
          !(banner.TextBlock || '').toLowerCase().includes(this.filters.TextBlock.toLowerCase())
        )
          return false
        if (this.filters.DateFrom) {
          const filterDate = moment(this.filters.DateFrom, 'YYYY-MM-DD').startOf('day')
          const bannerDate = moment.utc(banner.DateFrom)
          if (!bannerDate.isValid() || bannerDate.isBefore(filterDate)) return false
        }
        if (
          this.filters.UpdatedBy &&
          !(banner.UpdatedBy || '').toLowerCase().includes(this.filters.UpdatedBy.toLowerCase())
        )
          return false
        return true
      })
    },
    activeFilterChips() {
      const chips = []
      if (this.filters.DateToFrom) {
        chips.push({
          key: 'DateToFrom',
          label: `Einddatum ≥ ${moment(this.filters.DateToFrom).format('DD-MM-YYYY')}`,
        })
      }
      if (this.filters.BrandCode) {
        chips.push({
          key: 'BrandCode',
          label: `Merk: ${this.getBrandName(this.filters.BrandCode)}`,
        })
      }
      if (this.filters.BackgroundColor) {
        chips.push({
          key: 'BackgroundColor',
          label: `Kleur: ${this.translateColor(this.filters.BackgroundColor)}`,
        })
      }
      if (this.filters.TextBlock) {
        chips.push({ key: 'TextBlock', label: `Tekst: "${this.filters.TextBlock}"` })
      }
      if (this.filters.DateFrom) {
        chips.push({
          key: 'DateFrom',
          label: `Begindatum ≥ ${moment(this.filters.DateFrom).format('DD-MM-YYYY')}`,
        })
      }
      if (this.filters.DateTo) {
        chips.push({
          key: 'DateTo',
          label: `Einddatum ≤ ${moment(this.filters.DateTo).format('DD-MM-YYYY')}`,
        })
      }
      if (this.filters.UpdatedBy) {
        chips.push({ key: 'UpdatedBy', label: `Gewijzigd door: "${this.filters.UpdatedBy}"` })
      }
      return chips
    },
    sortedBanners() {
      if (!this.sortColumn) return this.filteredBanners
      const col = this.sortColumn
      const dir = this.sortDirection === 'asc' ? 1 : -1
      return [...this.filteredBanners].sort((a, b) => {
        let valA = a[col] ?? ''
        let valB = b[col] ?? ''
        if (col === 'DateFrom' || col === 'DateTo') {
          valA = valA ? moment.utc(valA).valueOf() : dir === 1 ? Infinity : -Infinity
          valB = valB ? moment.utc(valB).valueOf() : dir === 1 ? Infinity : -Infinity
          return (valA - valB) * dir
        }
        return String(valA).localeCompare(String(valB), 'nl') * dir
      })
    },
    pagedBanners() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.sortedBanners.slice(start, start + this.pageSize)
    },
    // Sum of the declared column widths plus the actions column. Without a
    // definite table width, `table-layout: fixed` lets cell content inflate the
    // columns instead of the other way round.
    tableMinWidth() {
      const columns = this.tableColumns.reduce(
        (sum, col) => sum + (parseFloat(col.width) || 140),
        0
      )
      return `${columns + ACTIONS_COLUMN_WIDTH}px`
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.currentPage = 1
      },
    },
  },
  mounted() {
    this.loadBanners()
  },
  methods: {
    async loadBanners() {
      this.loading = true
      this.error = null
      try {
        const response = await warningBannerService.getAllWarningBanners()
        this.banners = response.data || []
      } catch (error) {
        console.error('Failed to load banners:', error)
        this.error = 'Het laden van de waarschuwingsbanners is mislukt. Probeer het opnieuw.'
      } finally {
        this.loading = false
      }
    },
    onCreate() {
      this.selectedBanner = null
      this.showForm = true
    },
    onEdit(banner) {
      this.selectedBanner = {
        Id: banner.Id,
        BrandCode: banner.BrandCode,
        BackgroundColor: banner.BackgroundColor,
        TextBlock: banner.TextBlock,
        DateFrom: banner.DateFrom,
        DateTo: banner.DateTo,
      }
      this.showForm = true
    },
    onDeleteClick(banner) {
      this.deleteConfirmBanner = banner
    },
    async onDeleteConfirm() {
      this.deleteInProgress = true
      try {
        await warningBannerService.deleteWarningBanner(this.deleteConfirmBanner.Id)
        this.banners = this.banners.filter((b) => b.Id !== this.deleteConfirmBanner.Id)
        this.deleteConfirmBanner = null
      } catch (e) {
        this.error = 'Er is een fout opgetreden bij het verwijderen van de banner.'
      } finally {
        this.deleteInProgress = false
      }
    },
    closeForm() {
      this.showForm = false
      this.selectedBanner = null
    },
    onBannerSaved() {
      this.closeForm()
      this.loadBanners()
    },
    setFilter(key, value) {
      this.filters[key] = value
    },
    clearMultipleFilters(keys) {
      keys.forEach((key) => {
        this.filters[key] = ''
      })
    },
    toggleSort(column) {
      if (this.sortColumn === column) {
        if (this.sortDirection === 'asc') {
          this.sortDirection = 'desc'
        } else {
          this.sortColumn = null
          this.sortDirection = 'asc'
        }
      } else {
        this.sortColumn = column
        this.sortDirection = 'asc'
      }
    },
    removeFilter(key) {
      this.filters[key] = ''
    },
    clearFilters() {
      this.filters = {
        BrandCode: '',
        BackgroundColor: '',
        TextBlock: '',
        DateFrom: '',
        DateTo: '',
        DateToFrom: '',
        UpdatedBy: '',
      }
    },
    getBrandBadgeClass,
    getBrandName(brandCode) {
      return BRAND_MAP[brandCode] || brandCode
    },
    translateColor(color) {
      return COLOR_MAP[color] || color
    },
    formatDate(dateString) {
      if (!dateString) return null
      try {
        return moment.utc(dateString).format('DD-MM-YYYY HH:mm')
      } catch (error) {
        return dateString
      }
    },
    truncateText(text) {
      if (!text) return ''
      return text.length > 100 ? text.substring(0, 100) + '...' : text
    },
    // The cell shows the rendered banner markup, so the tooltip needs the plain
    // text behind it rather than the tags.
    stripHtml(html) {
      if (!html) return ''
      const el = document.createElement('div')
      el.innerHTML = html
      return el.textContent || ''
    },
  },
}
</script>

<style lang="scss" scoped>
// Layout only. The table, badges, buttons, pagination, dialog and toast all
// come from the design system (styles/ds/). See styles/ds/README.md before
// adding anything here.

.warning-banner-page__text {
  max-width: 340px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--ds-font-size-sm);
  color: var(--ds-color-text-muted);

  :deep(a) {
    color: var(--ds-color-brand);
  }
}

.warning-banner-page__nodate {
  color: var(--ds-color-text-subtle);
  font-style: italic;
}
</style>
